import { ApiErrorMessage, BaseApi } from "api";
import { endpoints } from "config";
import {
  GetLayerParams,
  GetLayerParsedResponse,
  GetLayerResponse,
} from "./layerModels";
import { parseUrlParams } from "shared/helpers/urlHelpers";

class LayersApi extends BaseApi {
  /**
   * Parse errors from `getLayer`
   */
  private static getLayerParseError: typeof this.parseGenericErrorMessageCode =
    (msg) => {
      switch (msg) {
        // API-specific error codes here, generic messages handled below
        default:
          return this.parseGenericErrorMessageCode(msg);
      }
    };
  /**
   * Parse errors from `getLayer`
   */
  private static getLayerParseSuccess = (
    apiResponse: GetLayerResponse
  ): GetLayerParsedResponse => {
    // If nothing is returned, early return an empty array
    if ("Layers" in apiResponse) {
      const showfeatures = parseUrlParams<boolean>("showfeatures", false, true);
      return apiResponse.Layers.map((group, groupIndex) => ({
        orderNumber: groupIndex,
        name: group.LayerName,
        groupName: `feature_line_${groupIndex}` as const,
        visible: showfeatures,
        children: group.Entries.map((layer, layerIndex) => ({
          orderNumber: layerIndex + 1,
          targetLayer: `feature_line_${groupIndex}_${layerIndex}` as const,
          name: `${group.LayerName} - ${layerIndex + 1}`,
          points: layer.Points,
          // TODO: Parse tags earlier or enforce into a set order
          details: layer.Tags,
          type: layer.GeometryType,
          // First and last points are the same, we could assume it's a polygon
          // type:
          //   layer.Points[0].atv === layer.Points[layer.Points.length - 1].atv &&
          //   layer.Points[0].ath === layer.Points[layer.Points.length - 1].ath
          //     ? "polygon"
          //     : layer.GeometryType,
          //
        }))
          // TEMP: Filter out "point" types for now
          // REASON: They are not styled nicely in krpano yet, they also clutter the UI
          .filter((l) => l.type !== "point"),
        // Filter out Groups with no layers
      })).filter((group) => group.children.length > 0);
    }
    return [];
  };

  /**
   * Get the Overpass API layers values for the panorama
   */
  static getLayer = async (params: GetLayerParams) => {
    try {
      const result = await this.makeRequest<
        GetLayerResponse,
        ApiErrorMessage[]
      >({
        url: endpoints.features.getLayer,
        method: "GET",
        config: {
          params,
        },
      });
      if (this.isResponseSuccessful(result)) {
        return this.successResponse(
          this.getLayerParseSuccess(result.data.details)
        );
      } else {
        return this.errorResponse(
          this.getLayerParseError(result.data.errors[0].message_code)
        );
      }
    } catch (err: unknown) {
      return this.safelyCatchUnknownError<GetLayerResponse>(
        err,
        "getLayer",
        this.getLayerParseError
      );
    }
  };
}

export default LayersApi;
